import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaCalendarAlt, FaUser, FaTag, FaHeart, FaComment, FaShare, FaInstagram, FaTwitter, FaYoutube, FaBookmark, FaPrint, FaEnvelope, FaArrowUp } from 'react-icons/fa';
import Header from './Header';
import Footer from './Footer';
import PDFViewer from './PDFViewer';
import { API_ENDPOINTS } from '../config/api';
import { getImageUrl } from '../utils/imageUtils';


// Add CSS styles for React Quill content rendering
const quillStyles = `
  /* React Quill Size Classes - These are the actual classes Quill generates */
  .ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  .ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  /* Target elements with size classes */
  .ql-editor .ql-size-small,
  .card-content .ql-size-small,
  div .ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  .ql-editor .ql-size-large,
  .card-content .ql-size-large,
  div .ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .ql-editor .ql-size-huge,
  .card-content .ql-size-huge,
  div .ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  /* Target specific elements that might have size classes */
  .ql-editor p.ql-size-huge,
  .card-content p.ql-size-huge,
  div p.ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
    margin-bottom: 1.5rem !important;
  }
  
  .ql-editor p.ql-size-large,
  .card-content p.ql-size-large,
  div p.ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
    margin-bottom: 1.25rem !important;
  }
  
  .ql-editor p.ql-size-small,
  .card-content p.ql-size-small,
  div p.ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
    margin-bottom: 0.75rem !important;
  }
  
  /* Target headings with size classes */
  .ql-editor h1.ql-size-huge,
  .ql-editor h2.ql-size-huge,
  .ql-editor h3.ql-size-huge,
  .card-content h1.ql-size-huge,
  .card-content h2.ql-size-huge,
  .card-content h3.ql-size-huge,
  div h1.ql-size-huge,
  div h2.ql-size-huge,
  div h3.ql-size-huge {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  .ql-editor h1.ql-size-large,
  .ql-editor h2.ql-size-large,
  .ql-editor h3.ql-size-large,
  .card-content h1.ql-size-large,
  .card-content h2.ql-size-large,
  .card-content h3.ql-size-large,
  div h1.ql-size-large,
  div h2.ql-size-large,
  div h3.ql-size-large {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  .ql-editor h1.ql-size-small,
  .ql-editor h2.ql-size-small,
  .ql-editor h3.ql-size-small,
  .card-content h1.ql-size-small,
  .card-content h2.ql-size-small,
  .card-content h3.ql-size-small,
  div h1.ql-size-small,
  div h2.ql-size-small,
  div h3.ql-size-small {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  /* Universal selector to catch any element with size classes */
  *[class*="ql-size-huge"] {
    font-size: 2.5rem !important;
    line-height: 1.2 !important;
    font-weight: 600 !important;
  }
  
  *[class*="ql-size-large"] {
    font-size: 1.5rem !important;
    line-height: 1.3 !important;
  }
  
  *[class*="ql-size-small"] {
    font-size: 0.75rem !important;
    line-height: 1.4 !important;
  }
  
  /* Responsive content container for imported HTML */
  .publication-content {
    width: 100%;
  }
  
  /* Make images responsive and mobile-friendly */
  .publication-content img,
  .ql-editor img,
  .card-content img,
  div img {
    max-width: 100% !important;
    width: 100% !important;
    height: auto !important;
    display: block;
    margin: 0px;
    object-fit: contain;
    border-radius: 8px;
  }
  
  /* Handle figures and captions */
  .publication-content figure {
    margin: 1rem 0;
  }
  .publication-content figcaption {
    text-align: center;
    font-size: 0.9rem;
    color: #6b7280;
    margin-top: 0.5rem;
  }
  
  /* Responsive iframes and videos */
  .publication-content iframe,
  .publication-content video {
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    aspect-ratio: 16 / 9;
    display: block;
    border: 0;
    border-radius: 8px;
  }
  
  /* Tables should scroll horizontally on small screens */
  .publication-content table {
    width: 100% !important;
    border-collapse: collapse;
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .publication-content th,
  .publication-content td {
    padding: 0.5rem;
  }
  
  /* Related Articles Image Size Enforcement */
  .related-articles img {
    height: 140px !important;
    min-height: 140px !important;
    max-height: 140px !important;
    width: 100% !important;
    object-fit: cover !important;
  }
  
  /* Mobile view - Remove fixed height for related article cards */
  @media (max-width: 768px) {
    .related-articles > div > div {
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
    }
    
    /* Target the specific card div with inline styles */
    .related-articles > div > div[style*="height: 280px"] {
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
    }
    
    /* Also target by the specific style attribute pattern */
    .related-articles > div > div[style*="280px"] {
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
    }
    
    /* Universal override for all related article cards on mobile */
    .related-articles > div > div {
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
    }
    
    /* Force override any inline height styles */
    .related-articles div[style*="height"] {
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
    }
    
    /* Aggressive override for any element with 280px height */
    .related-articles *[style*="280px"] {
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
    }
  }
  
  /* Extra mobile tweaks */
  @media (max-width: 576px) {
    .publication-content img {
      margin: 0px;
      border-radius: 6px;
    }
    .publication-content iframe,
    .publication-content video {
      aspect-ratio: 16 / 9;
      min-height: 180px;
    }
  }
  
  /* Sidebar sticky positioning fix for publications */
  .publication-sidebar {
    position: sticky !important;
    top: 7rem !important;
    z-index: 999 !important;
    max-height: none !important;
    overflow-y: visible !important;
  }
  
  
  /* Ensure sidebar doesn't overlap with header */
  @media (min-width: 992px) {
    .publication-sidebar {
      top: 7rem !important;
      max-height: none !important;
    }
  }
  
  /* Mobile responsive sidebar positioning */
  @media (max-width: 991px) {
    .publication-sidebar {
      position: static !important;
      top: auto !important;
      max-height: none !important;
      overflow-y: visible !important;
    }
  }
  
  /* Mobile view - Reduce padding for publication articles */
  @media (max-width: 768px) {
    .publication-article-content {
      padding: 1rem !important;
    }
    
    .publication-hero-overlay {
      padding: 1rem 1rem 1rem !important;
    }
    
    .publication-hero-no-image {
      padding: 1rem !important;
    }
  }
`;

const PublicPublicationDetail = () => {
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [relatedPublications, setRelatedPublications] = useState([]);
  const [htmlImported, setHtmlImported] = useState(false);

  const { identifier } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  useEffect(() => {
    fetchPublication();
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [identifier]);

  useEffect(() => {
    if (publication) {
      fetchRelatedPublications();
      // Check if publication content contains HTML and hide sidebar accordingly
      if (publication.content) {
        const hasHtml = detectHtmlContent(publication.content);
        setHtmlImported(hasHtml);
      }
    }
  }, [publication]);

  // Check for HTML content when component mounts
  useEffect(() => {
    if (publication && publication.content) {
      const hasHtml = detectHtmlContent(publication.content);
      setHtmlImported(hasHtml);
    }
  }, [publication]);

  const handleScroll = () => {
    const scrollTop = window.pageYOffset;
    const docHeight = document.body.offsetHeight - window.innerHeight;
    const scrollPercent = (scrollTop / docHeight) * 100;
    setReadingProgress(scrollPercent);
    setShowScrollTop(scrollTop > 300);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to detect if publication content is imported HTML (complex) vs rich text editor content (simple)
  const detectHtmlContent = (content) => {
    if (!content) return false;
    
    // Check if content contains HTML tags
    const htmlTagRegex = /<[^>]*>/;
    if (!htmlTagRegex.test(content)) return false;
    
    // Count HTML tags to determine complexity
    const tagMatches = content.match(/<[^>]*>/g) || [];
    const tagCount = tagMatches.length;
    
    // Check for complex HTML structure indicators
    const hasComplexStructure = (
      content.includes('<html') ||
      content.includes('<head') ||
      content.includes('<body') ||
      content.includes('<div') ||
      content.includes('<section') ||
      content.includes('<article') ||
      content.includes('<header') ||
      content.includes('<footer') ||
      content.includes('<nav') ||
      content.includes('<aside') ||
      content.includes('<main') ||
      content.includes('<figure') ||
      content.includes('<table') ||
      content.includes('<form') ||
      content.includes('<iframe') ||
      content.includes('<video') ||
      content.includes('<audio') ||
      content.includes('<canvas') ||
      content.includes('<svg')
    );
    
    // Check for rich text editor indicators (simple formatting)
    const hasSimpleFormatting = (
      content.includes('<p>') ||
      content.includes('<h1>') ||
      content.includes('<h2>') ||
      content.includes('<h3>') ||
      content.includes('<h4>') ||
      content.includes('<h5>') ||
      content.includes('<h6>') ||
      content.includes('<strong>') ||
      content.includes('<em>') ||
      content.includes('<u>') ||
      content.includes('<ul>') ||
      content.includes('<ol>') ||
      content.includes('<li>') ||
      content.includes('<blockquote>') ||
      content.includes('<code>') ||
      content.includes('<pre>') ||
      content.includes('<span>') ||
      content.includes('<br>') ||
      content.includes('<hr>')
    );
    
    // If it has complex structure, it's imported HTML
    if (hasComplexStructure) return true;
    
    // If it has many tags but only simple formatting, it's likely rich text editor content
    if (tagCount > 10 && hasSimpleFormatting && !hasComplexStructure) return false;
    
    // If it has complex tags, it's imported HTML
    if (hasComplexStructure) return true;
    
    // Default: treat as rich text editor content (show sidebar)
    const result = false;
    
    // Debug logging
    console.log('🔍 HTML Content Detection Debug:');
    console.log('  - Tag count:', tagCount);
    console.log('  - Has complex structure:', hasComplexStructure);
    console.log('  - Has simple formatting:', hasSimpleFormatting);
    console.log('  - Final result (isImportedHTML):', result);
    console.log('  - Content preview:', content.substring(0, 200) + '...');
    
    return result;
  };

  const fetchPublication = async () => {
    try {
      // Check if identifier is a number (ID) or string (custom URL)
      const isNumeric = !isNaN(identifier) && !isNaN(parseFloat(identifier));
      
      let pub;
      
      if (isNumeric) {
        // Use the test endpoint that doesn't require authentication
        const response = await axios.get(API_ENDPOINTS.TEST_PUBLICATIONS);
        const publications = response.data.publications || response.data;
        pub = publications.find(p => p.id == identifier);
      } else {
        // Use the public endpoint for custom URLs
        const response = await axios.get(`${API_ENDPOINTS.PUBLIC_PUBLICATION}/${identifier}`);
        pub = response.data;
      }
      
      if (pub) {
        setPublication(pub);
      } else {
        setError('Publication not found');
      }
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch publication');
      setLoading(false);
    }
  };

  const fetchRelatedPublications = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.TEST_PUBLICATIONS);
      const allPublications = response.data.publications || response.data;
      
      // Debug: Check the structure of publications
      console.log('=== DEBUGGING RELATED PUBLICATIONS ===');
      console.log('Current URL identifier:', identifier);
      console.log('Current publication:', publication);
      console.log('Current publication ID:', publication.id);
      console.log('Current publication title:', publication.title);
      console.log('Current publication publication_type_name:', publication.publication_type_name);
      console.log('All publications from API:', allPublications);
      
      // Get the current publication's publication type
      const currentPublicationType = publication.publication_type_name;
      
      console.log('Current publication type being used for filtering:', currentPublicationType);
      
      // Filter publications by same publication type only, excluding current publication
      const related = allPublications
        .filter(pub => {
          const pubPublicationType = pub.publication_type_name;
          const isSamePublicationType = pubPublicationType === currentPublicationType;
          const isNotCurrent = pub.id !== publication.id;
          console.log(`Checking pub ${pub.id} (${pub.title}): publication_type=${pubPublicationType}, currentPublicationType=${currentPublicationType}, same=${isSamePublicationType}, notCurrent=${isNotCurrent}`);
          return isNotCurrent && isSamePublicationType;
        })
        .sort((a, b) => new Date(b.published_date) - new Date(a.published_date)) // Sort by latest first
        .slice(0, 2); // Limit to 2 latest publications
      
      console.log('Related publications found:', related);
      console.log('=== END DEBUGGING ===');
      setRelatedPublications(related);
    } catch (err) {
      console.error('Failed to fetch related publications:', err);
    }
  };

  const getImageUrlWithFallback = (imagePath, publicationType) => {
    if (imagePath) {
      return getImageUrl(imagePath);
    }
    
    // Return empty string if no image - no fallback to external services
    return '';
  };

  // Helper function to generate the correct back navigation URL based on publication type
  const getBackNavigationUrl = () => {
    if (!publication) return '/publications';
    
    // Generate back URL based on publication type
    switch (publication.publication_type_name) {
              case 'Research Papers':
          return '/research-papers';
              case 'Newsletters':
          return '/newsletters';
              case 'Blogs':
          return '/blogs';
      default:
        return '/publications';
    }
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
      <div className="text-center">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading publication...</p>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="container mt-5">
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">❌ Error Loading Publication</h4>
        <p>{error}</p>
        <button 
          className="btn btn-outline-danger"
          onClick={() => window.location.href = getBackNavigationUrl()}
        >
          Back to {publication?.publication_type_name || 'Publications'}
        </button>
      </div>
    </div>
  );

  if (!publication) return null;

  const imageUrl = getImageUrlWithFallback(publication.image_path, publication.publication_type_name);

  return (
    <>
      {/* Add React Quill styles for proper font rendering */}
      <style dangerouslySetInnerHTML={{ __html: quillStyles }} />
      
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
        fontFamily: '"Quicksand", sans-serif',
        position: 'relative'
      }}>
      {/* Reading Progress Bar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: `${readingProgress}%`,
        height: '4px',
        background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4)',
        zIndex: 1000,
        transition: 'width 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }} />

      {/* Floating Action Buttons */}
      <div style={{
        position: 'fixed',
        right: '2rem',
        bottom: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        zIndex: 999
      }}>
        {/* Bookmark Button */}
        <button
          onClick={() => setIsBookmarked(!isBookmarked)}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: '2px solid #ff6b6b',
            background: isBookmarked ? '#ff6b6b' : 'rgba(255,255,255,0.9)',
            color: isBookmarked ? 'white' : '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1) rotate(5deg)';
            e.target.style.boxShadow = '0 8px 30px rgba(0,0,0,0.25)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) rotate(0deg)';
            e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
          }}
          title="Bookmark this article"
        >
          <div style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: isBookmarked ? 'white' : '#ff6b6b',
            lineHeight: '1'
          }}>
            🔖
          </div>
        </button>

        {/* Print Button */}
        <button
          onClick={() => window.print()}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: '2px solid #3b82f6',
            background: 'rgba(255,255,255,0.9)',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 8px 30px rgba(0,0,0,0.25)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
          }}
          title="Print this article"
        >
          <div style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: '#3b82f6',
            lineHeight: '1'
          }}>
            🖨️
          </div>
        </button>

        {/* Share Button */}
        <button
          onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: publication.title,
                url: window.location.href
              });
            } else {
              navigator.clipboard.writeText(window.location.href);
              alert('Link copied to clipboard!');
            }
          }}
          style={{
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            border: '2px solid #10b981',
            background: 'rgba(255,255,255,0.9)',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            backdropFilter: 'blur(10px)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.boxShadow = '0 8px 30px rgba(0,0,0,0.25)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
          }}
          title="Share this article"
        >
          <div style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: '#10b981',
            lineHeight: '1'
          }}>
            📤
          </div>
        </button>

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              border: '2px solid #f59e0b',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              animation: 'bounceIn 0.6s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.1) translateY(-5px)';
              e.target.style.boxShadow = '0 8px 30px rgba(0,0,0,0.25)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1) translateY(0)';
              e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
            }}
            title="Scroll to top"
          >
            <div style={{ 
              fontSize: '20px', 
              fontWeight: 'bold',
              color: 'white',
              lineHeight: '1'
            }}>
              ⬆️
            </div>
          </button>
        )}
      </div>

      {/* Header Navigation */}
      <Header />

      {/* Back to Publications Button */}
      <div className="container mb-4" style={{ paddingTop: '2rem' }}>
        <div className="row">
          <div className="col-12">
            <button 
              className="btn btn-light d-inline-flex align-items-center gap-2"
              onClick={() => window.location.href = getBackNavigationUrl()}
              style={{
                borderRadius: '12px',
                padding: '12px 20px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                border: 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
              }}
            >
              <FaArrowLeft /> Back to {publication?.publication_type_name || 'Publications'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container" style={{ paddingBottom: '4rem' }}>
        <div className="row">
          {/* Main Content Area */}
          <div className={htmlImported ? "col-12" : "col-lg-9"}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
              marginBottom: '2rem',
              position: 'relative',
              animation: 'slideUpFade 0.8s ease'
            }}>
                            {/* Publication Image - Only show if image exists */}
              {imageUrl ? (
                <div style={{ position: 'relative', width: '100%' }}>
                  <img 
                    src={imageUrl}
                    alt={publication.title}
                    style={{
                      width: '100%',
                      height: '450px',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                      // Remove broken image - no fallback to external services
                      e.target.style.display = 'none';
                    }}
                  />
                  
                  {/* Overlay Content */}
                  <div className="publication-hero-overlay" style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    padding: '3rem 2rem 2rem',
                    color: 'white'
                  }}>
                    <h1 style={{ 
                      fontSize: '2.8rem', 
                      fontWeight: '700',
                      color: 'white',
                      margin: 0,
                      lineHeight: '1.2',
                      textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
                      animation: 'fadeInUp 1s ease 0.3s both'
                    }}>
                      {publication.title}
                    </h1>
                    

                  </div>
                </div>
              ) : (
                /* Title and Subtitle when no image - show at top of content area */
                <div className="publication-hero-no-image" style={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  padding: '3rem 2rem',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <h1 style={{ 
                    fontSize: '2.8rem', 
                    fontWeight: '700',
                    color: 'white',
                    margin: 0,
                    lineHeight: '1.2',
                    textShadow: '2px 2px 8px rgba(0,0,0,0.3)',
                    animation: 'fadeInUp 1s ease 0.3s both'
                  }}>
                    {publication.title}
                  </h1>
                  

                </div>
              )}

              {/* Article Content */}
              <div className="publication-article-content" style={{ padding: '3rem' }} ref={contentRef}>
                {/* Article Body */}
                <div style={{
                  fontSize: '1.1rem',
                  lineHeight: '1.8',
                  color: '#374151'
                }}>
                  {publication.content ? (
                    <div className="publication-content" dangerouslySetInnerHTML={{ __html: publication.content }} />
                  ) : (
                    <div>
                      <p style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: '600', 
                        color: '#1f2937',
                        marginBottom: '1.5rem',
                        borderLeft: '4px solid #667eea',
                        paddingLeft: '1rem'
                      }}>
                        Why This Matters More Than Ever
                      </p>
                      <p style={{ marginBottom: '1.5rem' }}>
                        In today's dynamic market, the landscape is constantly evolving. Technology advances at breakneck speed, 
                        customer behavior shifts rapidly, and competition intensifies daily. What separates growing businesses 
                        isn't a perfect plan — it's <strong>adaptability, belief in possibility, and a bias for action.</strong>
                      </p>
                      <p style={{ marginBottom: '1.5rem' }}>
                        When the founder evolves, the business follows. This publication explores the mindset shifts that 
                        drive sustainable growth and long-term success in an ever-changing business environment.
                      </p>
                      <p style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: '600', 
                        color: '#1f2937',
                        marginBottom: '1rem',
                        borderLeft: '4px solid #667eea',
                        paddingLeft: '1rem'
                      }}>
                        Signs Your Mindset Might Be Holding the Business Back
                      </p>
                      <p>
                        Understanding the key indicators that suggest your current mindset may be limiting your business 
                        potential is crucial for growth and development.
                      </p>
                    </div>
                  )}
                </div>

                {/* PDF Viewer Section */}
                {publication.file_path && (
                  <div className="mt-5">
                    <h4 className="mb-3" style={{ 
                      color: '#1f2937', 
                      fontWeight: '600',
                      borderBottom: '2px solid #e5e7eb',
                      paddingBottom: '0.5rem'
                    }}>
                      📄 PDF Document
                    </h4>
                    <PDFViewer 
                      pdfUrl={publication.file_path} 
                      title={publication.title}
                      showControls={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>



          {/* Right Sidebar - Only show when HTML is not imported */}
          {/* 
            Sidebar visibility logic:
            - htmlImported = true: Hide sidebar, show full-width content (for imported HTML files)
            - htmlImported = false: Show sidebar, show 9-column content (for rich text editor content)
          */}
          {!htmlImported && (
            <div className="col-lg-3">
              <div className="publication-sidebar">
              {/* Date Section */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '16px',
                padding: '1.5rem',
                marginTop: '1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                animation: 'slideInRight 0.8s ease 0.2s both',
                border: '1px solid #e2e8f0'
              }}>
                <FaCalendarAlt style={{ color: '#64748b', fontSize: '1.2rem' }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: '#64748b', fontWeight: '500', fontSize: '0.875rem' }}>
                    Published on
                  </span>
                  <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '1rem' }}>
                    {publication.published_date ? new Date(publication.published_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'No date'}
                  </span>
                </div>
              </div>

              {/* Social Sharing Section */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                animation: 'slideInRight 0.8s ease 0.4s both'
              }}>
                <h4 style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: '600', 
                  color: '#1f2937',
                  marginBottom: '1rem'
                }}>
                  Show some love!
                </h4>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <a 
                    href="https://www.instagram.com/umoor.iqtesadiyah?igshid=MzRlODBiNWFlZA%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #833AB4, #FD1D1D)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      textDecoration: 'none',
                      boxShadow: '0 4px 15px rgba(131, 58, 180, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.1) rotate(5deg)';
                      e.target.style.boxShadow = '0 8px 25px rgba(131, 58, 180, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1) rotate(0deg)';
                      e.target.style.boxShadow = '0 4px 15px rgba(131, 58, 180, 0.3)';
                    }}
                  >
                    <FaInstagram />
                  </a>
                  <a 
                    href="https://x.com/umooriqtesadiya?t=aPfr1rQc-pjdPWxZbUEH5g&s=31"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #000000, #333333)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      textDecoration: 'none',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.1) rotate(-5deg)';
                      e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1) rotate(0deg)';
                      e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.3)';
                    }}
                  >
                    <div style={{ 
                      fontSize: '20px', 
                      fontWeight: 'bold',
                      color: 'white',
                      lineHeight: '1'
                    }}>
                      𝕏
                    </div>
                  </a>
                  <a 
                    href="https://www.youtube.com/@UmoorIqtesadiyah?si=Z36O3_WgkJuJHt1D"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #FF0000, #CC0000)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      textDecoration: 'none',
                      boxShadow: '0 4px 15px rgba(255, 0, 0, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.1) rotate(5deg)';
                      e.target.style.boxShadow = '0 8px 25px rgba(255, 0, 0, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1) rotate(0deg)';
                      e.target.style.boxShadow = '0 4px 15px rgba(255, 0, 0, 0.3)';
                    }}
                  >
                    <FaYoutube />
                  </a>
                </div>
              </div>

              {/* Tags/Categories Section */}
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                animation: 'slideInRight 0.8s ease 0.6s both'
              }}>
                <h4 style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: '600', 
                  color: '#1f2937',
                  marginBottom: '1rem'
                }}>
                  Categories
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {publication.category_name && (
                    <span style={{
                      background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                      color: '#1e40af',
                      padding: '0.75rem 1rem',
                      borderRadius: '25px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      boxShadow: '0 2px 8px rgba(30, 64, 175, 0.2)',
                      transition: 'all 0.3s ease'
                    }}>
                      {publication.category_name}
                    </span>
                  )}
                  <span style={{
                    background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                    color: '#1e40af',
                    padding: '0.75rem 1rem',
                    borderRadius: '25px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(30, 64, 175, 0.2)',
                    transition: 'all 0.3s ease'
                  }}>
                    {publication.publication_type_name}
                  </span>
                  <span style={{
                    background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                    color: '#1e40af',
                    padding: '0.75rem 1rem',
                    borderRadius: '25px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(30, 64, 175, 0.2)',
                    transition: 'all 0.3s ease'
                  }}>
                    Business
                  </span>
                </div>
              </div>

              {/* Related Articles Section */}
              <div style={{
                background: 'white',
                borderRadius: '20px',
                padding: '2rem',
                marginBottom: '1.5rem',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)',
                animation: 'slideInRight 0.8s ease 0.8s both',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <h4 style={{ 
                  fontSize: '1.2rem', 
                  fontWeight: '700', 
                  color: '#1f2937',
                  marginBottom: '1.5rem',
                  textAlign: 'center',
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  Related {publication.publication_type_name} Articles
                </h4>
                {relatedPublications.length > 0 ? (
                  <div className="related-articles" style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '1.25rem' // Increased gap for better visual separation
                  }}>
                    {relatedPublications.map((relatedPub, index) => {
                      const relatedImageUrl = getImageUrl(relatedPub.image_path, relatedPub.publication_type_name);
                      return (
                        <div
                          key={relatedPub.id}
                          style={{
                            background: 'white',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            border: '1px solid #e2e8f0',
                            height: '280px', // Fixed height for consistency
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.1)';
                          }}
                          onClick={() => window.location.href = `/publications/${relatedPub.custom_url || relatedPub.id}`}
                        >
                          {/* Article Image */}
                          <div style={{ 
                            position: 'relative', 
                            width: '100%', 
                            height: '140px !important',
                            minHeight: '140px !important',
                            maxHeight: '140px !important',
                            flexShrink: 0 // Prevent image from shrinking
                          }}>
                            <img 
                              src={relatedImageUrl}
                              alt={relatedPub.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={(e) => {
                                // Remove broken image - no fallback to external services
                                e.target.style.display = 'none';
                              }}
                            />

                          </div>
                          
                          {/* Article Content - Flexbox for consistent button positioning */}
                          <div style={{ 
                            padding: '1rem',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          }}>
                            {/* Title Section */}
                            <div style={{ flex: 1 }}>
                              <h5 style={{
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#1f2937',
                                margin: '0 0 0.75rem 0',
                                lineHeight: '1.3',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minHeight: '2.6rem' // Fixed height for title area
                              }}>
                                {relatedPub.title}
                              </h5>
                            </div>
                            
                            {/* Read Article Button - Always at bottom */}
                            <div style={{ marginTop: 'auto' }}>
                              <button
                                style={{
                                  width: '100%',
                                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '0.75rem',
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                                }}
                              >
                                Read Article
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem 1rem',
                    color: '#6b7280'
                  }}>
                    <div style={{
                      fontSize: '3rem',
                      marginBottom: '1rem',
                      opacity: '0.5'
                    }}>
                      📭
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}>
                      No other {publication.category_name} articles available at the moment.
                    </p>
                    <p style={{
                      margin: '0.5rem 0 0 0',
                      fontSize: '0.8rem',
                      opacity: '0.7'
                    }}>
                      Check back later for more content in this category.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes slideUpFade {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeInLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes bounceIn {
            from {
              opacity: 0;
              transform: scale(0.3);
            }
            50% {
              opacity: 1;
              transform: scale(1.05);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
      
      {/* Footer */}
      <Footer />
    </div>
    </>
  );
};

// Helper functions
function getTypeGradient(type) {
  switch (type) {
            case 'Newsletters': return 'linear-gradient(to bottom, #2F80ED, #56CCF2)';
    case 'Technical Report': return 'linear-gradient(to bottom, #11998e, #38ef7d)';
            case 'Research Papers': return 'linear-gradient(to bottom, #667eea, #764ba2)';
    case 'Policy Document': return 'linear-gradient(to bottom, #f093fb, #f5576c)';
    case 'Annual Report': return 'linear-gradient(to bottom, #4facfe, #00f2fe)';
    case 'White Paper': return 'linear-gradient(to bottom, #43e97b, #38f9d7)';
    case 'Highlight': return 'linear-gradient(to bottom, #fa709a, #fee140)';
    default: return 'linear-gradient(to bottom, #667eea, #764ba2)';
  }
}



export default PublicPublicationDetail; 